import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import './PendingLenders.css';

interface PendingLender {
  nmlsId: string;
  status: 'not_in_system' | 'not_enriched';
  lenderName: string;
  companyName: string;
}

interface GetPendingLendersResponse {
  success: boolean;
  pendingLenders: PendingLender[];
  totalOffersNmlsIds: number;
  totalEnriched: number;
  totalExistingLenders: number;
}

type EnrichState = 'idle' | 'loading' | 'success' | 'error';

const ENRICH_URL = 'https://us-central1-score-my-offer.cloudfunctions.net/enrichLender';

const PendingLenders: React.FC = () => {
  const location = useLocation();
  const prefetched = (location.state as any)?.prefetched ?? null;

  const [pendingLenders, setPendingLenders] = useState<PendingLender[]>(
    prefetched ? (prefetched.pendingLenders as PendingLender[]) : []
  );
  const [stats, setStats] = useState({
    totalFromOffers: prefetched?.totalOffersNmlsIds ?? 0,
    enriched: prefetched?.totalEnriched ?? 0,
  });
  const [isLoading, setIsLoading] = useState(!prefetched);
  const [error, setError] = useState<string | null>(null);
  const [enrichStates, setEnrichStates] = useState<Record<string, EnrichState>>({});

  const fetchPendingLenders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<unknown, GetPendingLendersResponse>(functions, 'getPendingLenders');
      const result = await fn();
      if (result.data.success) {
        setPendingLenders(result.data.pendingLenders);
        setStats({
          totalFromOffers: result.data.totalOffersNmlsIds,
          enriched: result.data.totalEnriched,
        });
      } else {
        setError('Failed to fetch pending lenders');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch pending lenders');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!prefetched) fetchPendingLenders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnrich = async (nmlsId: string) => {
    setEnrichStates(prev => ({ ...prev, [nmlsId]: 'loading' }));
    try {
      const res = await fetch(ENRICH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nmlsId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setEnrichStates(prev => ({ ...prev, [nmlsId]: 'success' }));
      // Remove from list after a short delay so user sees the success state
      setTimeout(() => {
        setPendingLenders(prev => prev.filter(l => l.nmlsId !== nmlsId));
        setStats(prev => ({ ...prev, enriched: prev.enriched + 1 }));
      }, 1500);
    } catch (err) {
      console.error('Enrich error:', err);
      setEnrichStates(prev => ({ ...prev, [nmlsId]: 'error' }));
    }
  };

  const notInSystem = pendingLenders.filter(l => l.status === 'not_in_system');
  const notEnriched = pendingLenders.filter(l => l.status === 'not_enriched');

  return (
    <div className="pl-container">

      {/* ── Header ── */}
      <div className="pl-header">
        <div>
          <h1 className="pl-title">Pending Lenders</h1>
          <p className="pl-subtitle">
            NMLS IDs from submitted offers that still need enrichment in the system
          </p>
        </div>
        <button onClick={fetchPendingLenders} className="pl-refresh-btn" disabled={isLoading}>
          {isLoading ? 'Loading…' : '↺ Refresh'}
        </button>
      </div>

      {/* ── Stats ── */}
      {!isLoading && (
        <div className="pl-stats-row">
          <div className="pl-stat-card">
            <span className="pl-stat-value">{stats.totalFromOffers}</span>
            <span className="pl-stat-label">Total NMLS IDs in Offers</span>
          </div>
          <div className="pl-stat-card pl-stat-green">
            <span className="pl-stat-value">{stats.enriched}</span>
            <span className="pl-stat-label">Enriched</span>
          </div>
          <div className="pl-stat-card pl-stat-amber">
            <span className="pl-stat-value">{pendingLenders.length}</span>
            <span className="pl-stat-label">Pending</span>
          </div>
          <div className="pl-stat-card pl-stat-red">
            <span className="pl-stat-value">{notInSystem.length}</span>
            <span className="pl-stat-label">Not in System</span>
          </div>
          <div className="pl-stat-card pl-stat-blue">
            <span className="pl-stat-value">{notEnriched.length}</span>
            <span className="pl-stat-label">In System, Not Enriched</span>
          </div>
        </div>
      )}

      {error && (
        <div className="pl-error">⚠ {error}</div>
      )}

      {isLoading ? (
        <div className="pl-loading">
          <div className="pl-spinner" />
          <p>Checking for pending lenders…</p>
        </div>
      ) : pendingLenders.length === 0 ? (
        <div className="pl-empty">
          <div className="pl-empty-icon">✅</div>
          <h3>All Caught Up!</h3>
          <p>All lenders from submitted offers have been enriched.</p>
        </div>
      ) : (
        <div className="pl-table-wrapper">
          <table className="pl-table">
            <thead>
              <tr>
                <th>NMLS #</th>
                <th>Name</th>
                <th>Company</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingLenders.map((lender) => {
                const enrichState = enrichStates[lender.nmlsId] || 'idle';
                return (
                  <tr key={lender.nmlsId} className={enrichState === 'success' ? 'pl-row-success' : ''}>
                    <td>
                      <span className="pl-nmls-badge">NMLS #{lender.nmlsId}</span>
                    </td>
                    <td className="pl-name-cell">{lender.lenderName || <span className="pl-na">—</span>}</td>
                    <td className="pl-company-cell">{lender.companyName || <span className="pl-na">—</span>}</td>
                    <td>
                      {lender.status === 'not_in_system' ? (
                        <span className="pl-status-badge pl-status-missing">Not in System</span>
                      ) : (
                        <span className="pl-status-badge pl-status-unenriched">Needs Enrichment</span>
                      )}
                    </td>
                    <td>
                      <div className="pl-actions">
                        {enrichState === 'success' ? (
                          <span className="pl-action-success">✓ Enriched</span>
                        ) : enrichState === 'error' ? (
                          <>
                            <span className="pl-action-error">Failed</span>
                            <button className="pl-enrich-btn" onClick={() => handleEnrich(lender.nmlsId)}>
                              Retry
                            </button>
                          </>
                        ) : (
                          <button
                            className="pl-enrich-btn"
                            onClick={() => handleEnrich(lender.nmlsId)}
                            disabled={enrichState === 'loading'}
                          >
                            {enrichState === 'loading' ? 'Enriching…' : 'Enrich'}
                          </button>
                        )}
                        <a
                          href={`https://nmlsconsumeraccess.org/EntityDetails.aspx/INDIVIDUAL/${lender.nmlsId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pl-nmls-link"
                        >
                          NMLS Profile ↗
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PendingLenders;
